import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api.js';
import Spinner from '../components/Spinner.jsx';
import { topicLabel } from '../constants/topics.js';

function GaugeBar({ pct }) {
  const color = pct >= 75 ? 'from-teal-400 to-teal-500'
              : pct >= 50 ? 'from-ocean-400 to-ocean-500'
              :              'from-coral-400 to-coral-500';
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-3 bg-ocean-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${color} transition-all duration-700`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-sm font-mono font-semibold w-10 text-right dive-value">{pct} %</span>
    </div>
  );
}

export default function QuizStats() {
  const { id } = useParams();
  const [data, setData]         = useState(null);
  const [lernData, setLernData] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    Promise.all([api.getQuizStats(id), api.getLernreiheProgress(id)])
      .then(([quiz, lern]) => { setData(quiz); setLernData(lern); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center gap-3">
        <Link to={`/ausbilder/schueler/${id}`} className="text-sm text-ocean-600 hover:underline">← Zurück</Link>
        <h1 className="text-xl font-bold text-gray-900">Quiz-Auswertung</h1>
      </div>

      {loading && <div className="flex justify-center py-12"><Spinner size="lg" /></div>}
      {error   && <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>}

      {/* Lernreihe-Fortschritt */}
      {lernData && lernData.lernreihen.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-ocean-600">Lernreihen</h2>
          {lernData.lernreihen.map((lr) => (
            <div key={lr.id} className="card space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-800">{lr.title}</span>
                <span className="text-xs text-gray-400 font-mono">
                  <span className="dive-value">{lr.done_steps}</span> / {lr.total_steps} Schritte
                </span>
              </div>
              <GaugeBar pct={lr.pct} />
              {lr.last_activity && (
                <p className="text-xs text-gray-400">Zuletzt aktiv: {new Date(lr.last_activity).toLocaleDateString('de-DE')}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {data && (
        <>
          {/* Gesamtübersicht */}
          <div className="card-ocean flex items-center gap-6 px-5 py-4">
            <div className="text-center">
              <div className="text-3xl font-mono font-bold dive-value">{data.attempts_total}</div>
              <div className="text-xs text-gray-500 mt-0.5">Versuche gesamt</div>
            </div>
            {data.stats.length > 0 && (() => {
              const total   = data.stats.reduce((s, r) => s + r.total, 0);
              const correct = data.stats.reduce((s, r) => s + r.correct, 0);
              const pct     = total > 0 ? Math.round((correct / total) * 100) : 0;
              return (
                <div className="text-center">
                  <div className="text-3xl font-mono font-bold text-teal-600">{pct} %</div>
                  <div className="text-xs text-gray-500 mt-0.5">Gesamtquote</div>
                </div>
              );
            })()}
          </div>

          {data.stats.length === 0 && (
            <div className="card text-center py-10 text-sm text-gray-500">
              Noch keine Quiz-Versuche für diesen Schüler.
            </div>
          )}

          {data.stats.length > 0 && (
            <div className="space-y-3">
              {data.stats
                .slice()
                .sort((a, b) => a.pct - b.pct)
                .map((r) => (
                  <div key={r.topic} className="card space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-800">
                        {topicLabel(r.topic)}
                      </span>
                      <span className="text-xs text-gray-400 font-mono">
                        <span className="text-teal-600">{r.correct}</span> / {r.total} richtig
                      </span>
                    </div>
                    <GaugeBar pct={r.pct} />
                    {r.pct < 60 && (
                      <p className="text-xs text-coral-500 flex items-center gap-1">
                        ⚠ Schwachstelle — Wiederholung empfohlen
                      </p>
                    )}
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
